import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Camera, Shield, MapPin, Users, FlaskConical, Building2,
  Check, ChevronRight, ChevronLeft, Loader2, Sparkles,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

export interface AAKConsentDetails {
  basic_aak: boolean;
  spatial_sharing: boolean;
  cross_farmer: boolean;
  research_training: boolean;
  commercial_licensing: boolean;
}

const DEFAULT_CONSENT: AAKConsentDetails = {
  basic_aak: true,
  spatial_sharing: false,
  cross_farmer: false,
  research_training: false,
  commercial_licensing: false,
};

type Mode = 'onboarding' | 'settings';
type ScreenIndex = 1 | 2 | 3;

interface AAKConsentJourneyProps {
  mode?: Mode;
  startScreen?: ScreenIndex;
  onComplete?: () => void;
  onSkip?: () => void;
}

export const AAKConsentJourney = ({
  mode: modeProp,
  startScreen: startProp,
  onComplete,
  onSkip,
}: AAKConsentJourneyProps = {}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  // Resolve mode/startScreen from props or route state
  const routeState = (location.state ?? {}) as { mode?: Mode; startScreen?: ScreenIndex };
  const mode: Mode = modeProp ?? routeState.mode ?? 'settings';
  const initialScreen: ScreenIndex = startProp ?? routeState.startScreen ?? 1;

  const [screen, setScreen] = useState<ScreenIndex>(initialScreen);
  const [consent, setConsent] = useState<AAKConsentDetails>(DEFAULT_CONSENT);
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState<keyof AAKConsentDetails | null>(null);

  // Load current consent
  useEffect(() => {
    const load = async () => {
      if (!user?.id) {
        setLoading(false);
        return;
      }
      const { data, error } = await supabase
        .from('profiles')
        .select('aak_consent_details')
        .eq('id', user.id)
        .single();

      if (!error && data?.aak_consent_details) {
        setConsent({ ...DEFAULT_CONSENT, ...(data.aak_consent_details as Partial<AAKConsentDetails>) });
      }
      setLoading(false);
    };
    load();
  }, [user?.id]);

  const updateConsent = async (key: keyof AAKConsentDetails, value: boolean) => {
    if (!user?.id) return;
    const previous = consent[key];
    const next = { ...consent, [key]: value };
    setConsent(next);
    setSavingKey(key);

    const { error } = await supabase
      .from('profiles')
      .update({ aak_consent_details: next })
      .eq('id', user.id);

    setSavingKey(null);

    if (error) {
      // Revert
      setConsent({ ...consent, [key]: previous });
      toast.error('Could not save', { description: 'Please check your connection and try again.' });
      return;
    }

    if ('vibrate' in navigator) navigator.vibrate(15);
    toast.success('Preference saved');
  };

  const handleFinish = () => {
    if (onComplete) {
      onComplete();
      return;
    }
    if (mode === 'onboarding') {
      localStorage.setItem('onboarding_completed', 'true');
      navigate('/', { replace: true });
    } else {
      navigate('/settings', { replace: true });
    }
  };

  const handleSkip = () => {
    if (onSkip) {
      onSkip();
      return;
    }
    if (mode === 'onboarding') {
      localStorage.setItem('onboarding_completed', 'true');
      navigate('/', { replace: true });
    } else {
      navigate('/settings', { replace: true });
    }
  };

  const goNext = () => setScreen((s) => (s < 3 ? ((s + 1) as ScreenIndex) : s));
  const goBack = () => {
    if (screen > 1) {
      setScreen((s) => ((s - 1) as ScreenIndex));
    } else if (mode === 'settings') {
      navigate(-1);
    }
  };

  const skipToPreferences = () => setScreen(2);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header / progress */}
      <div className="px-4 pt-[calc(1rem+env(safe-area-inset-top))] pb-3 flex items-center gap-3">
        <button
          onClick={goBack}
          className="w-10 h-10 flex items-center justify-center rounded-full bg-muted active:scale-95"
          aria-label="Back"
        >
          <ChevronLeft className="w-5 h-5 text-foreground" />
        </button>
        <div className="flex-1 flex items-center gap-1.5">
          {[1, 2, 3].map((n) => (
            <div
              key={n}
              className={cn(
                'h-1.5 flex-1 rounded-full transition-colors',
                n <= screen ? 'bg-primary' : 'bg-muted',
              )}
            />
          ))}
        </div>
        <span className="text-xs text-muted-foreground tabular-nums">{screen}/3</span>
      </div>

      {/* Screens */}
      <div className="flex-1 overflow-y-auto px-4 pb-6">
        {screen === 1 && <Screen1 onSkipToPrefs={mode === 'settings' ? skipToPreferences : undefined} />}
        {screen === 2 && (
          <Screen2
            consent={consent}
            savingKey={savingKey}
            onChange={updateConsent}
          />
        )}
        {screen === 3 && <Screen3 />}
      </div>

      {/* Footer actions */}
      <div className="px-4 pb-[calc(1.5rem+env(safe-area-inset-bottom))] pt-3 space-y-2 border-t border-border bg-background">
        {screen < 3 ? (
          <Button onClick={goNext} variant="primary" size="lg" className="w-full h-12">
            Next
            <ChevronRight className="w-5 h-5" />
          </Button>
        ) : (
          <Button onClick={handleFinish} variant="primary" size="lg" className="w-full h-12">
            <Check className="w-5 h-5" />
            Done
          </Button>
        )}

        {mode === 'onboarding' && screen >= 2 && (
          <Button onClick={handleSkip} variant="ghost" size="lg" className="w-full">
            Skip for now
          </Button>
        )}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// Screen 1 — Educational
// ─────────────────────────────────────────────────────────────
const Screen1 = ({ onSkipToPrefs }: { onSkipToPrefs?: () => void }) => (
  <div className="max-w-md mx-auto pt-2 space-y-6">
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
    >
      <h1 className="text-2xl font-bold text-foreground">Your Photos Help You</h1>
    </motion.div>

    {/* Illustration */}
    <motion.div
      initial={{ scale: 0.9, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ delay: 0.1, duration: 0.4 }}
      className="relative h-48 rounded-3xl bg-gradient-to-br from-primary/10 to-success/10 border border-primary/20 flex items-center justify-center overflow-hidden"
    >
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,hsl(var(--primary)/0.08),transparent_50%)]" />
      <div className="relative flex items-center gap-4">
        <div className="w-20 h-20 rounded-2xl bg-background shadow-lg flex items-center justify-center">
          <Camera className="w-10 h-10 text-primary" strokeWidth={1.5} />
        </div>
        <ChevronRight className="w-6 h-6 text-muted-foreground" />
        <div className="w-20 h-20 rounded-2xl bg-background shadow-lg flex items-center justify-center text-4xl">
          🐔
        </div>
        <div className="absolute -top-2 -right-2 w-8 h-8 rounded-full bg-success flex items-center justify-center shadow-lg">
          <Check className="w-5 h-5 text-success-foreground" strokeWidth={3} />
        </div>
      </div>
    </motion.div>

    <p className="text-base text-foreground leading-relaxed">
      Every photo you take helps us understand your flock better. We use your photos to:
    </p>

    <div className="space-y-3">
      {[
        { icon: '📈', text: 'Track your flock health over time' },
        { icon: '⭐', text: 'Improve your credit score' },
        { icon: '💡', text: 'Give you better farming advice' },
      ].map((item, i) => (
        <motion.div
          key={i}
          initial={{ opacity: 0, x: -10 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.2 + i * 0.08 }}
          className="flex items-start gap-3 p-3 rounded-xl bg-card border border-border"
        >
          <span className="text-2xl shrink-0" aria-hidden>{item.icon}</span>
          <span className="text-sm text-foreground pt-0.5">{item.text}</span>
        </motion.div>
      ))}
    </div>

    <Card className="p-4 bg-success/5 border-success/30">
      <div className="flex items-start gap-3">
        <Shield className="w-5 h-5 text-success shrink-0 mt-0.5" />
        <div className="space-y-1">
          <p className="text-sm font-medium text-foreground">
            Your photos are private.
          </p>
          <p className="text-xs text-muted-foreground">
            Only you and your integrator can see them.
          </p>
        </div>
      </div>
    </Card>

    <div className="flex items-center justify-center gap-2 py-2">
      <Check className="w-4 h-4 text-success" />
      <p className="text-xs text-muted-foreground text-center">
        This is already active — no action needed.
      </p>
    </div>

    {onSkipToPrefs && (
      <button
        onClick={onSkipToPrefs}
        className="w-full text-sm text-primary font-medium underline-offset-4 hover:underline py-2"
      >
        Skip to preferences →
      </button>
    )}
  </div>
);

// ─────────────────────────────────────────────────────────────
// Screen 2 — Active toggles
// ─────────────────────────────────────────────────────────────
interface ToggleRowProps {
  icon: typeof MapPin;
  iconColor: string;
  iconBg: string;
  label: string;
  description: string;
  benefit: string;
  checked: boolean;
  saving: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
  comingSoon?: boolean;
  note?: string;
}

const ToggleRow = ({
  icon: Icon, iconColor, iconBg, label, description, benefit,
  checked, saving, onChange, disabled, comingSoon, note,
}: ToggleRowProps) => (
  <Card className={cn('p-4 transition-opacity', disabled && 'opacity-60')}>
    <div className="flex items-start gap-3">
      <div className={cn('w-12 h-12 rounded-2xl flex items-center justify-center shrink-0', iconBg)}>
        <Icon className={cn('w-6 h-6', iconColor)} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-sm font-semibold text-foreground">{label}</h3>
              {comingSoon && (
                <Badge variant="secondary" className="text-[10px] uppercase tracking-wide">
                  Coming Soon
                </Badge>
              )}
            </div>
            <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
              {description}
            </p>
          </div>
          <div className="shrink-0 flex items-center gap-2">
            {saving && <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />}
            <Switch
              checked={checked}
              onCheckedChange={onChange}
              disabled={disabled || saving}
              className="scale-125"
            />
          </div>
        </div>
        <div className="mt-3 flex items-start gap-2 p-2.5 rounded-lg bg-primary/5 border border-primary/10">
          <Sparkles className="w-3.5 h-3.5 text-primary shrink-0 mt-0.5" />
          <p className="text-xs text-foreground/80">{benefit}</p>
        </div>
        {note && (
          <p className="text-[11px] text-muted-foreground mt-2 italic">{note}</p>
        )}
      </div>
    </div>
  </Card>
);

const Screen2 = ({
  consent, savingKey, onChange,
}: {
  consent: AAKConsentDetails;
  savingKey: keyof AAKConsentDetails | null;
  onChange: (k: keyof AAKConsentDetails, v: boolean) => void;
}) => (
  <div className="max-w-md mx-auto pt-2 space-y-5">
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
    >
      <h1 className="text-2xl font-bold text-foreground">Help Your Community</h1>
    </motion.div>

    <p className="text-sm text-foreground leading-relaxed">
      When you share, everyone benefits. Your data helps farmers in your area get better advice.
    </p>

    <div className="space-y-3">
      <ToggleRow
        icon={MapPin}
        iconColor="text-info"
        iconBg="bg-info/10"
        label="Share my photo location"
        description="Your photo locations help create regional poultry health maps. Your exact location is rounded to protect privacy (~1km grid)."
        benefit="Farmers who share see regional disease alerts"
        checked={consent.spatial_sharing}
        saving={savingKey === 'spatial_sharing'}
        onChange={(v) => onChange('spatial_sharing', v)}
      />

      <ToggleRow
        icon={Users}
        iconColor="text-success"
        iconBg="bg-success/10"
        label="Compare my photos with other farmers"
        description="Let the AI compare your flock photos with other farmers in your area to spot problems early and share what works."
        benefit="Get personalized tips from top-performing farms nearby"
        checked={consent.cross_farmer}
        saving={savingKey === 'cross_farmer'}
        onChange={(v) => onChange('cross_farmer', v)}
      />
    </div>
  </div>
);

// ─────────────────────────────────────────────────────────────
// Screen 3 — Coming soon placeholders
// ─────────────────────────────────────────────────────────────
const Screen3 = () => (
  <div className="max-w-md mx-auto pt-2 space-y-5">
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
    >
      <h1 className="text-2xl font-bold text-foreground">Build the Future</h1>
    </motion.div>

    <p className="text-sm text-foreground leading-relaxed">
      Soon you'll be able to contribute to agricultural research and help train AI models that benefit all farmers.
    </p>

    <div className="space-y-3">
      <ToggleRow
        icon={FlaskConical}
        iconColor="text-warning"
        iconBg="bg-warning/10"
        label="Contribute to research"
        description="Your de-identified photos help train poultry intelligence models for all farmers."
        benefit="Research contributors will get early access to disease outbreak alerts"
        checked={false}
        saving={false}
        onChange={() => {}}
        disabled
        comingSoon
      />

      <ToggleRow
        icon={Building2}
        iconColor="text-primary"
        iconBg="bg-primary/10"
        label="Commercial data sharing"
        description="Your de-identified photos can be included in licensed datasets for agricultural research institutions and insurance companies."
        benefit="Commercial contributors will receive credit score bonuses"
        checked={false}
        saving={false}
        onChange={() => {}}
        disabled
        comingSoon
      />
    </div>

    <Card className="p-3 bg-muted/40 border-dashed">
      <p className="text-xs text-muted-foreground text-center">
        We'll notify you when these become available.
      </p>
    </Card>
  </div>
);

// ─────────────────────────────────────────────────────────────
// Helper: derive consent tier from JSONB details
// ─────────────────────────────────────────────────────────────
export type ConsentTier = 'basic' | 'community' | 'research';

export const getConsentTier = (details: Partial<AAKConsentDetails> | null | undefined): ConsentTier => {
  if (!details) return 'basic';
  if (details.research_training) return 'research';
  if (details.spatial_sharing || details.cross_farmer) return 'community';
  return 'basic';
};

export default AAKConsentJourney;
